import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// Connects to data-controller="engage--tour"
// Multi-step tour with spotlight overlay — step navigation, keyboard support.
export default class extends Controller {
  static targets = [
    "backdrop",
    "highlight",
    "panel",
    "content",
    "nextBtn",
    "skipContainer",
    "skipBtn",
    "progressIndicator",
  ]
  static values = {
    flowId: String,
    steps: Array,
    currentIndex: { type: Number, default: 0 },
  }

  #escHandler = null
  #abortController = null

  connect() {
    this.#abortController = new AbortController()
    void this.#startTour()
    this.#escHandler = (e) => {
      if (e.key === "Escape") void this.dismiss()
    }
    document.addEventListener("keydown", this.#escHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.#escHandler)
    this.#abortController?.abort()
    this.#clearSpotlight()
  }

  async next() {
    const step = this.#currentStep()
    if (!step) return

    await this.#patch(
      `/api/v1/engage/flow_states/${this.flowIdValue}/complete_step`,
      {
        step_key: step.key,
      },
    )

    if (this.currentIndexValue < this.stepsValue.length - 1) {
      this.currentIndexValue += 1
      this.#render()
    } else {
      this.element.remove()
    }
  }

  async skip() {
    const step = this.#currentStep()
    if (!step?.skippable) return

    await this.#patch(
      `/api/v1/engage/flow_states/${this.flowIdValue}/skip_step`,
      {
        step_key: step.key,
      },
    )

    if (this.currentIndexValue < this.stepsValue.length - 1) {
      this.currentIndexValue += 1
      this.#render()
    } else {
      await this.dismiss()
    }
  }

  async dismiss() {
    await this.#patch(`/api/v1/engage/flow_states/${this.flowIdValue}/dismiss`)
    this.element.remove()
  }

  clickBackdrop() {
    void this.dismiss()
  }

  // ── Private ──────────────────────────────────────────────────────────────

  async #startTour() {
    await this.#patch(`/api/v1/engage/flow_states/${this.flowIdValue}/start`)
    this.#render()
  }

  #currentStep() {
    return this.stepsValue[this.currentIndexValue] ?? null
  }

  #render() {
    const step = this.#currentStep()
    if (!step) return

    const isLast = this.currentIndexValue === this.stepsValue.length - 1
    const total = this.stepsValue.length

    // Update step content
    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = this.#renderStepContent(step)
    }

    // Update next button label
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.textContent = isLast ? "Finish" : "Next →"
    }

    // Update skip visibility
    if (this.hasSkipContainerTarget) {
      this.skipContainerTarget.innerHTML =
        step.skippable && !isLast
          ? `<button type="button"
               class="text-xs text-foreground-muted hover:text-foreground transition-colors"
               data-action="click->engage--tour#skip">Skip</button>`
          : ""
    }

    // Update progress dots
    if (this.hasProgressIndicatorTarget) {
      this.progressIndicatorTarget.innerHTML = this.#renderDots(
        this.currentIndexValue,
        total,
      )
    }

    // Show panel + spotlight
    this.#positionPanel(step)
    this.#updateSpotlight(step)
  }

  #renderStepContent(step) {
    let html = '<div class="space-y-3">'
    if (step.image_url) {
      html += `<img src="${this.#esc(step.image_url)}" alt="${this.#esc(step.title)}"
                    class="max-h-48 w-full rounded-lg object-cover" />`
    }
    html += `<div><h3 class="text-base leading-snug font-semibold text-foreground">${this.#esc(step.title)}</h3>`
    if (step.subtitle) {
      html += `<p class="mt-1 text-sm text-foreground-medium">${this.#esc(step.subtitle)}</p>`
    }
    html += "</div>"
    if (step.body) {
      html += `<div class="prose prose-sm max-w-none text-sm text-foreground-medium">${this.#esc(step.body)}</div>`
    }
    html += "</div>"
    return html
  }

  #renderDots(current, total) {
    let html = '<div class="flex items-center gap-1.5">'
    for (let i = 0; i < total; i++) {
      html += `<span class="${
        i === current
          ? "w-4 h-1.5 bg-primary rounded-full"
          : "w-1.5 h-1.5 bg-border rounded-full"
      } transition-all duration-200"></span>`
    }
    html += "</div>"
    return html
  }

  #positionPanel(step) {
    if (!this.hasPanelTarget) return

    const panel = this.panelTarget
    panel.style.display = ""

    const target = step.target_selector
      ? document.querySelector(step.target_selector)
      : null

    if (target) {
      const rect = target.getBoundingClientRect()
      panel.style.position = "fixed"
      panel.style.top = `${rect.bottom + 12}px`
      panel.style.left = `${Math.min(rect.left, window.innerWidth - 340)}px`
      panel.style.transform = ""
    } else {
      // Center
      panel.style.position = "fixed"
      panel.style.top = "50%"
      panel.style.left = "50%"
      panel.style.transform = "translate(-50%, -50%)"
    }

    // Show backdrop
    if (this.hasBackdropTarget) this.backdropTarget.style.display = ""
  }

  #updateSpotlight(step) {
    if (!this.hasHighlightTarget) return

    const selector = step.target_selector
    if (!selector) {
      this.highlightTarget.style.display = "none"
      return
    }

    const el = document.querySelector(selector)
    if (!el) {
      this.highlightTarget.style.display = "none"
      return
    }

    const rect = el.getBoundingClientRect()
    const pad = 4
    Object.assign(this.highlightTarget.style, {
      display: "",
      top: `${rect.top - pad}px`,
      left: `${rect.left - pad}px`,
      width: `${rect.width + pad * 2}px`,
      height: `${rect.height + pad * 2}px`,
    })
  }

  #clearSpotlight() {
    if (this.hasBackdropTarget) this.backdropTarget.style.display = "none"
    if (this.hasHighlightTarget) this.highlightTarget.style.display = "none"
  }

  #esc(text) {
    return String(text ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  async #patch(url, body = null) {
    try {
      const options = { credentials: "include", responseKind: "json" }
      if (this.#abortController) options.signal = this.#abortController.signal
      if (body) options.body = body
      const response = await patch(url, options)
      if (!response.ok)
        console.error(`[Engage] API error ${response.statusCode}`, url)
      return response
    } catch (e) {
      if (e.name !== "AbortError") throw e
    }
  }
}
